import {
  type PollResponse,
  useCreatePoll,
  useEditPoll,
  usePoll,
} from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import { getPollValidationSchema } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/constants";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useQueryClient } from "react-query";

import {
  getPollDefaultValues,
  type PollFormValues,
} from "./getPollDefaultValues";

interface UsePollFormParams {
  isEdit?: boolean;
  onComplete: (poll: PollResponse) => void;
  pollSgid?: string;
}

export const usePollForm = ({
  isEdit = false,
  onComplete,
  pollSgid,
}: UsePollFormParams) => {
  const modalControls = useModal();
  const queryClient = useQueryClient();

  const { data: pollForEdit, isInitialLoading: isLoadingPollForEdit } = usePoll(
    {
      sgid: pollSgid,
      options: { enabled: !!pollSgid && isEdit },
    },
  );

  const closesAtRef = useRef<string | null | undefined>(undefined);
  closesAtRef.current = pollForEdit?.closes_at;

  const resolverRef = useRef<Resolver<PollFormValues> | undefined>(undefined);
  if (!resolverRef.current) {
    resolverRef.current = (values, context, options) =>
      (
        yupResolver(
          getPollValidationSchema(closesAtRef.current),
        ) as unknown as Resolver<PollFormValues>
      )(values, context, options);
  }

  const methods = useForm<PollFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getPollDefaultValues(),
    resolver: resolverRef.current,
  });

  useEffect(() => {
    if (!pollForEdit) return;
    methods.reset(getPollDefaultValues(pollForEdit));
    void methods.trigger();
  }, [pollForEdit, methods]);

  const { mutate: handleCreate, isPending: isCreating } = useCreatePoll({
    onSuccess: poll => {
      onComplete(poll);
      modalControls.remove();
    },
  });

  const { mutate: handleEdit, isPending: isEditing } = useEditPoll({
    onSuccess: poll => {
      queryClient.setQueryData(["poll", poll.sgid], poll);
      onComplete(poll);
      modalControls.remove();
    },
  });

  const onSubmit = (values: PollFormValues) => {
    const payload = {
      title: values.title,
      poll_options: values.poll_options,
      allow_members_to_view_results: values.allow_members_to_view_results,
      closes_at: values.enable_poll_duration
        ? (values.closes_at ?? undefined)
        : undefined,
    };

    if (isEdit && pollForEdit) {
      handleEdit({ ...payload, id: pollForEdit.id });
    } else {
      handleCreate(payload);
    }
  };

  return {
    canEditOptions: !isEdit || !pollForEdit?.results?.total_responses,
    isLoadingPollForEdit,
    isSubmitting: isCreating || isEditing,
    isValid: methods.formState.isValid,
    methods,
    onSubmit,
  };
};
